import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import { useUser } from '../context/UserContext';
import PageTitle from '../components/pageTitle';
import './signupRequests.css';

const ADMIN_EMAIL = 'davebradham@gmail.com';
const USERS_PER_PAGE = 20;

const SignupRequestsPage = () => {
    const { user } = useUser();
    const [requests, setRequests] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionMessage, setActionMessage] = useState('');
    const [fetchError, setFetchError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [editingUserId, setEditingUserId] = useState(null);
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        display: '',
        email: '',
        role: 'host',
        phone: ''
    });
    const [savingUserId, setSavingUserId] = useState(null);

    const isAdmin = user?.email === ADMIN_EMAIL;

    const loadRequests = async () => {
        setLoading(true);
        setActionMessage('');
        setFetchError('');
        try {
            const [pendingRes, usersRes] = await Promise.all([
                api.get('/user/pendingUsers'),
                api.get('/user/allUsers')
            ]);
            setRequests(pendingRes?.data || []);
            setUsers(usersRes?.data || []);
        } catch (e) {
            console.error('failed to load pending users', e);
            const msg = e?.response?.data?.error || 'Failed to load users data.';
            setFetchError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin) loadRequests();
    }, [isAdmin]);

    const filteredUsers = useMemo(() => {
        const term = String(searchTerm || '').trim().toLowerCase();
        if (!term) return users;
        return users.filter((u) => {
            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
            const display = String(u.display_name || '').toLowerCase();
            const email = String(u.email || '').toLowerCase();
            const role = String(u.role || '').toLowerCase();
            const phone = String(u.phone || '').toLowerCase();
            const country = String(u.country || '').toLowerCase();
            return name.includes(term) || display.includes(term) || email.includes(term) || role.includes(term) || phone.includes(term) || country.includes(term);
        });
    }, [users, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedUsers = useMemo(() => {
        const start = (currentPage - 1) * USERS_PER_PAGE;
        return filteredUsers.slice(start, start + USERS_PER_PAGE);
    }, [filteredUsers, currentPage]);

    const approve = async (id, email) => {
        try {
            await api.post(`/user/approveUser/${id}`);
            setActionMessage(`Approved: ${email}`);
            loadRequests();
        } catch (e) {
            const msg = e?.response?.data?.error || 'Failed to approve request.';
            setActionMessage(`Error: ${msg}`);
        }
    };

    const reject = async (id, email) => {
        if (!window.confirm(`Reject signup request from ${email}?`)) return;
        try {
            await api.delete(`/user/pendingUser/${id}`);
            setActionMessage(`Rejected: ${email}`);
            loadRequests();
        } catch (e) {
            setActionMessage('Failed to reject request.');
        }
    };

    const beginEdit = (u) => {
        setEditingUserId(u.id);
        setEditForm({
            firstName: String(u.first_name || ''),
            lastName: String(u.last_name || ''),
            display: String(u.display_name || ''),
            email: String(u.email || ''),
            role: String(u.role || 'host'),
            phone: String(u.phone || '')
        });
    };

    const cancelEdit = () => {
        setEditingUserId(null);
        setEditForm({ firstName: '', lastName: '', display: '', email: '', role: 'host', phone: '' });
    };

    const onEditFieldChange = (event) => {
        const { name, value } = event.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const saveUser = async (id) => {
        setSavingUserId(id);
        try {
            const response = await api.put(`/user/adminUser/${id}`, {
                firstName: String(editForm.firstName || '').trim(),
                lastName: String(editForm.lastName || '').trim(),
                display: String(editForm.display || '').trim(),
                email: String(editForm.email || '').trim(),
                role: String(editForm.role || 'host').trim(),
                phone: String(editForm.phone || '').trim()
            });

            const updated = response?.data || {};
            setUsers((prev) => prev.map((entry) => Number(entry.id) === Number(id) ? { ...entry, ...updated } : entry));
            setActionMessage(`Updated user: ${updated.email || editForm.email}`);
            cancelEdit();
        } catch (e) {
            const msg = e?.response?.data?.error || 'Failed to update user.';
            setActionMessage(`Error: ${msg}`);
        } finally {
            setSavingUserId(null);
        }
    };

    if (!user) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Please log in to access this page.</p>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div className="report-shell">
            <PageTitle title="Users" />

            {actionMessage && (
                <div className="app-notice app-notice-success" style={{ marginBottom: '1rem' }}>
                    {actionMessage}
                </div>
            )}

            {fetchError && (
                <div className="app-notice app-notice-error" style={{ marginBottom: '1rem' }}>
                    {fetchError}
                </div>
            )}

            <div className="report-card">
                <div className="report-card-header">
                    <h3>Pending Account Requests</h3>
                    <button className="btn-secondary" onClick={loadRequests} disabled={loading}>
                        {loading ? 'Loading…' : 'Refresh'}
                    </button>
                </div>

                {!loading && requests.length === 0 && (
                    <p style={{ color: '#666', padding: '1rem 0' }}>No pending signup requests.</p>
                )}

                {requests.length > 0 && (
                    <div className="report-table-wrap">
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Display</th>
                                    <th>Email</th>
                                    <th>Requested</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map(r => (
                                    <tr key={r.id}>
                                        <td>{r.first_name} {r.last_name}</td>
                                        <td>{r.display_name}</td>
                                        <td>{r.email}</td>
                                        <td>{r.requested_at ? new Date(r.requested_at).toLocaleString() : '—'}</td>
                                        <td>
                                            <button
                                                className="btn-primary"
                                                style={{ marginRight: '0.5rem', padding: '0.3rem 0.9rem', fontSize: '0.85rem' }}
                                                onClick={() => approve(r.id, r.email)}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                className="btn-danger"
                                                style={{ padding: '0.3rem 0.9rem', fontSize: '0.85rem' }}
                                                onClick={() => reject(r.id, r.email)}
                                            >
                                                Reject
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="report-card">
                <div className="report-card-header">
                    <h3>Existing Users</h3>
                </div>

                <div className="users-toolbar">
                    <input
                        className="users-search"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search by name, display, email, role, phone, or country"
                    />
                    <p className="users-count">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found</p>
                </div>

                {!loading && users.length === 0 && (
                    <p style={{ color: '#666', padding: '1rem 0' }}>No users found.</p>
                )}

                {users.length > 0 && (
                    <div className="report-table-wrap">
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Display</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Phone</th>
                                    <th>Country</th>
                                    <th>Created</th>
                                    <th>Last Login</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedUsers.map((u) => (
                                    <tr key={u.id}>
                                        <td>
                                            {editingUserId === u.id ? (
                                                <div className="users-name-edit-grid">
                                                    <input name="firstName" value={editForm.firstName} onChange={onEditFieldChange} placeholder="First" />
                                                    <input name="lastName" value={editForm.lastName} onChange={onEditFieldChange} placeholder="Last" />
                                                </div>
                                            ) : `${u.first_name || ''} ${u.last_name || ''}`}
                                        </td>
                                        <td>
                                            {editingUserId === u.id ? (
                                                <input className="users-inline-input" name="display" value={editForm.display} onChange={onEditFieldChange} />
                                            ) : (u.display_name || '—')}
                                        </td>
                                        <td>
                                            {editingUserId === u.id ? (
                                                <input className="users-inline-input" name="email" value={editForm.email} onChange={onEditFieldChange} />
                                            ) : u.email}
                                        </td>
                                        <td>
                                            {editingUserId === u.id ? (
                                                <select className="users-inline-input" name="role" value={editForm.role} onChange={onEditFieldChange}>
                                                    <option value="host">host</option>
                                                    <option value="guest">guest</option>
                                                </select>
                                            ) : (u.role || '—')}
                                        </td>
                                        <td>
                                            {editingUserId === u.id ? (
                                                <input className="users-inline-input" name="phone" value={editForm.phone} onChange={onEditFieldChange} />
                                            ) : (u.phone || '—')}
                                        </td>
                                        <td>{u.country || '—'}</td>
                                        <td>{u.created_at ? new Date(u.created_at).toLocaleString() : '—'}</td>
                                        <td>{u.last_login ? new Date(u.last_login).toLocaleString() : '—'}</td>
                                        <td>
                                            {editingUserId === u.id ? (
                                                <div className="users-row-actions">
                                                    <button className="btn-primary" onClick={() => saveUser(u.id)} disabled={savingUserId === u.id}>
                                                        {savingUserId === u.id ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button className="btn-secondary" onClick={cancelEdit} disabled={savingUserId === u.id}>Cancel</button>
                                                </div>
                                            ) : (
                                                <button className="btn-secondary" onClick={() => beginEdit(u)}>Edit</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {filteredUsers.length > USERS_PER_PAGE && (
                    <div className="users-pagination">
                        <button className="btn-secondary" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button className="btn-secondary" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignupRequestsPage;
